import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, TriangleAlert } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import { PrimaryButton, GhostButton } from "@/shared/components/settings-atoms";
import { ScoreBar } from "./ui-kit";
import { compareRuns } from "@/shared/lib/evals-engine";
import { verdict } from "@/shared/lib/evals-model";
import { DeltaChip, StatusPill } from "@/shared/components/evals-atoms";
import { evalKeys, startRun, toEvalRun, useEvalRun } from "@/shared/api/evals";

import type { Agent, EvalCompareRow, EvalRun, EvalSuite } from "@/shared/types";

/** What the gate holds once the pre-save run finishes. */
interface GateState {
  run: EvalRun;
  rows: EvalCompareRow[];
  base: EvalRun | null;
}

/** Where a check stands: still running (and how far), finished, or unable to run. */
interface Check {
  state: GateState | null;
  progress: string | null;
  failed: string | null;
}

/* The gate. Publishing, with the agent's "Run evals first" switch on, runs the
   suite against the NEW config first and shows what it costs before the version
   goes live.

   This is what turns the tab from a dashboard into a control. A dashboard is
   something you must remember to check; a gate is something you cannot ship
   past without looking. Every survey of abandoned eval tools says the same
   thing — enforcement beats a chart.

   It is a gate, not a wall: a regression can be shipped, deliberately, with the
   count of what broke on the button.

   `prepare` saves the edit into the draft, the suite runs on the draft frozen as
   an unpublished version (`draft: true`), and publishing afterwards promotes that
   very version. */

export default function PublishGate({ open, onOpenChange, agent, evals, baseRunId = null, prepare, onConfirm }: {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent;
  evals: EvalSuite;
  /** The run to compare with: its baseline, else its latest. */
  baseRunId?: string | null;
  /** Save the pending edit into the draft; false when it could not. */
  prepare?: () => Promise<boolean>;
  onConfirm: () => void;
}) {
  const label = `Pre-publish check · ${agent.version} → next`;
  const { state, progress, failed } = useDraftCheck(!!open, agent.id, label, baseRunId, prepare);

  const regressions = state ? state.rows.filter((r) => r.delta != null && r.delta < -0.005) : [];
  /* Everything worth looking at before shipping: cases that dropped against the
     baseline AND cases that are simply not passing. Listing only failures made
     the summary line contradict the list — "1 regressed" above a row showing a
     green +0.01, because the regressed case and the failing case weren't the
     same case. Worst delta first. */
  const attention = state
    ? [...new Set([...regressions.map((r) => r.caseId), ...state.run.results.filter((r) => r.status !== "pass").map((r) => r.caseId)])]
        // compareRuns builds a row per result, so every id here has one; the
        // filter below stays as the runtime belt-and-braces it already was.
        .map((id) => state.rows.find((r) => r.caseId === id)!)
        .filter(Boolean)
        .sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0))
    : [];
  const v = state ? verdict(state.run.score, state.base?.score) : "none";
  const caseName = (id: string) => state?.run.cases?.[id]?.name || evals.cases.find((c) => c.id === id)?.name || id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 block w-screen h-[100dvh] rounded-none md:w-[min(560px,94vw)] md:h-auto md:rounded-[16px] sm:rounded-[16px] max-w-none overflow-hidden bg-[var(--canvas)] border border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold tracking-[-0.01em] m-0">
            <ShieldCheck className="w-4 h-4 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            Check before publishing
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.6] text-[var(--text-tertiary)] mt-1 mb-0">
            Running {agent.name}&rsquo;s {evals.cases.length}-case suite against the draft before
            it goes live.
          </DialogDescription>
        </div>

        <div className="px-5 py-4 max-h-[56vh] overflow-y-auto">
          {failed ? (
            <div role="alert" className="flex items-start gap-2 px-3 py-2.5 rounded-[10px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)]">
              <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-[1px] text-[var(--warn-text)]" strokeWidth={1.75} />
              <span className="text-[12.5px] leading-[1.5] text-[var(--warn-text)]">
                The check could not run — {failed} You can still publish; nothing was measured.
              </span>
            </div>
          ) : !state ? (
            <div className="py-10 flex flex-col items-center gap-3">
              <div className="w-full h-[3px] rounded-full bg-[var(--surface-2)] overflow-hidden">
                <div className="h-full w-1/2 rounded-full bg-[var(--brand)] animate-pulse" />
              </div>
              <span role="status" className="text-[12.5px] text-[var(--text-tertiary)]">
                {progress ?? `Running ${evals.cases.length} cases…`}
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-3.5 py-3 rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)]">
                <div className="flex-1 min-w-0">
                  <div className="section-label">New score</div>
                  <div className="mt-1"><ScoreBar value={state.run.score} target={0.7} width={72} /></div>
                </div>
                <div className="text-right">
                  <div className="section-label">vs last run</div>
                  <div className="mt-1 flex justify-end">
                    <DeltaChip delta={state.base?.score != null && state.run.score != null ? +(state.run.score - state.base.score).toFixed(3) : null} />
                  </div>
                </div>
              </div>

              {regressions.length > 0 && (
                <div className="flex items-start gap-2 mt-3 px-3 py-2.5 rounded-[10px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)]">
                  <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-[1px] text-[var(--warn-text)]" strokeWidth={1.75} />
                  <span className="text-[12.5px] leading-[1.5] text-[var(--warn-text)]">
                    {regressions.length} case{regressions.length === 1 ? "" : "s"} scored lower than
                    the last run. Publish anyway if that trade is deliberate.
                  </span>
                </div>
              )}

              {attention.length > 0 && (
                <>
                  <div className="section-label mt-4 mb-1.5">Needs a look · {attention.length}</div>
                  <div className="rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] divide-y divide-[var(--hairline)]">
                    {attention.slice(0, 6).map((r) => {
                      const worst = [...r.checks].filter((c) => c.score != null).sort((a, b) => a.score! - b.score!)[0];
                      const why = worst?.evidence ?? r.error;
                      return (
                        <div key={r.caseId} className="flex items-start gap-3 px-3.5 py-2.5">
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-medium text-[var(--text-primary)] truncate">
                              {caseName(r.caseId)}
                            </span>
                            {why && (
                              <span className="block text-[11.5px] text-[var(--text-tertiary)] mt-[2px] truncate">
                                {why}
                              </span>
                            )}
                          </span>
                          <DeltaChip delta={r.delta} className="mt-[2px]" />
                          <StatusPill status={r.status} />
                        </div>
                      );
                    })}
                  </div>
                  {attention.length > 6 && (
                    <div className="text-[11.5px] text-[var(--text-quaternary)] mt-2">
                      {attention.length - 6} more — open the run in Evals for the full table.
                    </div>
                  )}
                </>
              )}

              {attention.length === 0 && (
                <div className="py-8 text-center">
                  <ShieldCheck className="w-5 h-5 mx-auto mb-2 text-[var(--success)]" strokeWidth={1.5} />
                  <div className="text-[14px] font-medium text-[var(--text-primary)]">Every case passed</div>
                  <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
                    {v === "improvement" ? "And the score went up." : "Nothing regressed."}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 px-5 py-3.5 border-t hairline">
          <GhostButton onClick={() => onOpenChange(false)}>Keep editing</GhostButton>
          <div className="flex-1" />
          <PrimaryButton disabled={!state && !failed} onClick={() => onConfirm()}>
            {regressions.length ? `Publish anyway · ${regressions.length} regressed` : "Publish"}
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Save the draft, run the suite on it, and follow the run until
   the eval worker has graded every case. Its own start, silent, because the
   gate draws the failure itself (rule book §3, "Nothing fails silently"). */
function useDraftCheck(
  active: boolean,
  agentId: string,
  label: string,
  baseRunId: string | null,
  prepare?: () => Promise<boolean>,
): Check {
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: startRun,
    onSettled: () => qc.invalidateQueries({ queryKey: evalKeys.all }),
    meta: { silent: true },
  });
  const [runId, setRunId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    setRunId(null);
    setFailed(null);
    if (!active) return;
    let gone = false;
    (async () => {
      if (prepare && !(await prepare())) {
        if (!gone) setFailed("the edit could not be saved to the draft.");
        return;
      }
      try {
        const run = await start.mutateAsync({ agent_id: agentId, draft: true, label });
        if (!gone) setRunId(run.id);
      } catch (e) {
        console.error(e);
        if (!gone) setFailed(`${e instanceof Error ? e.message : "the run did not start"}`);
      }
    })();
    return () => {
      gone = true;
    };
    // one check per opening: `start` and `prepare` are new objects every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, agentId]);

  const run = useEvalRun(active ? runId : null);
  const base = useEvalRun(active && baseRunId && baseRunId !== runId ? baseRunId : null);
  if (!run.data || run.data.status === "running") {
    const graded = run.data ? `Graded ${run.data.graded}/${run.data.total} cases…` : null;
    return { state: null, progress: graded, failed };
  }
  const checked = toEvalRun(run.data, run.data);
  const against = base.data ? toEvalRun(base.data, base.data) : null;
  return { state: { run: checked, rows: compareRuns(checked, against), base: against }, progress: null, failed };
}
